import { Button, Col, Descriptions, Form, Popconfirm, Row, Space } from 'antd';
import { type DescriptionsItemType } from 'antd/es/descriptions';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import WarehouseProductsTable from '../components/downloader/warehouse-products-table';
import { EMPTY_VALUE } from '../fragments/empty-value';
import SelectCategoryOptions from '../fragments/select-category-options';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { categoriesLoadAll } from '../reducers/categories/reducer';
import {
  downloaderLoadWarehouseCategory,
  downloaderLoadWarehouseProductsByWarehouseCategory,
  downloaderPairWarehouseCategory,
  downloaderUnpairWarehouseCategory,
} from '../reducers/downloader/reducer';
import { SPACING } from '../theme/tokens';

interface IForm {
  categoryUUID: string;
}

function DownloaderWarehouseCategory(): React.ReactElement {
  const dispatch = useAppDispatch();
  const downloaderWarehouseCategory = useSelector(
    (s: RootState) => s.Downloader.warehouse.category,
  );
  const downloaderUpdatedAt = useSelector(
    (s: RootState) => s.Downloader.updatedAt,
  );
  const downloaderInProcess = useSelector(
    (s: RootState) => s.Downloader.inProcess,
  );

  const { uuid } = useParams();
  const [formCategory] = Form.useForm<IForm>();

  const onFinishCategory = useCallback(
    (values: IForm): void => {
      if (!uuid) {
        return;
      }

      dispatch(downloaderPairWarehouseCategory(uuid, values.categoryUUID));
    },
    [uuid, dispatch],
  );

  const onUnpairCategory = useCallback((): void => {
    if (!uuid) {
      return;
    }

    dispatch(downloaderUnpairWarehouseCategory(uuid));
  }, [uuid, dispatch]);

  const reloadAll = useCallback((): void => {
    if (uuid) {
      dispatch(downloaderLoadWarehouseCategory(uuid));
      dispatch(downloaderLoadWarehouseProductsByWarehouseCategory(uuid));
    }

    dispatch(categoriesLoadAll());
  }, [uuid, dispatch]);

  useEffect((): void => {
    reloadAll();
  }, [reloadAll]);

  useEffect((): void => {
    formCategory.setFieldValue(
      'categoryUUID',
      downloaderWarehouseCategory?.category?.uuid,
    );
  }, [
    downloaderWarehouseCategory,
    downloaderUpdatedAt,
    downloaderInProcess,
    formCategory,
  ]);

  const items: DescriptionsItemType[] = [
    {
      key: '1',
      label: 'Raw Name',
      children: downloaderWarehouseCategory?.rawName || EMPTY_VALUE,
    },
    {
      key: '2',
      label: 'Name',
      children: downloaderWarehouseCategory?.name || EMPTY_VALUE,
    },
    {
      key: '3',
      label: 'Category',
      children: (
        <Form
          form={formCategory}
          disabled={downloaderInProcess || !uuid}
          onFinish={onFinishCategory}
        >
          <Row>
            <Col span={8}>
              <Form.Item name="categoryUUID">
                <SelectCategoryOptions
                  form={formCategory}
                  fieldName="categoryUUID"
                  isDisabled={downloaderInProcess}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Space size={SPACING.sm}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={downloaderInProcess}
                >
                  Save
                </Button>

                {downloaderWarehouseCategory?.category?.uuid && (
                  <Popconfirm
                    title="Unpair this category?"
                    onConfirm={onUnpairCategory}
                  >
                    <Button danger disabled={downloaderInProcess}>
                      Unpair
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </Col>
          </Row>
        </Form>
      ),
    },
  ];

  return (
    <>
      <DetailPageHeader
        title="Warehouse category"
        onReload={reloadAll}
        isReloadDisabled={downloaderInProcess}
      />

      <Descriptions
        bordered
        column={1}
        size="small"
        styles={{ content: { width: '75%' } }}
        items={items}
      />

      <WarehouseProductsTable />
    </>
  );
}

export default DownloaderWarehouseCategory;
